import { Rational, abs, add, div, equals, mul, pow, rat, root as rationalRoot, sub } from "./rational.ts";

/*
 * A tile's expression as a small tree (ADR 0127 §2). `evaluate` and `display`
 * both read this one tree, so the text a child sees and the value the
 * generator checked cannot disagree. Roots are of a whole number that is a perfect
 * square or cube, so they are exact too. Parentheses are not nodes: `display`
 * puts them in exactly where the tree's shape needs them ("(8 − 2) × 2"), and
 * a negative number follows an operator in brackets ("5 − (−7)").
 */
export type Op = "+" | "−" | "×" | "÷";

export type Node =
    | { kind: "int"; value: number }
    /** units / 10^places, shown as a decimal: 25/100 is "0.25". */
    | { kind: "dec"; units: number; places: number }
    /** A proper fraction shown with its glyph: ¾. */
    | { kind: "frac"; n: number; d: number }
    /** Only as the part in "25% of 48". */
    | { kind: "pct"; value: number }
    | { kind: "bin"; op: Op; left: Node; right: Node }
    | { kind: "pow"; base: Node; exp: 2 | 3 | 4 | 5 }
    /** √49 (index 2) or ∛64 (index 3): the radicand is a perfect square or cube. */
    | { kind: "root"; index: 2 | 3; radicand: Node }
    /** "¾ of 16", "25% of 48": a fraction or percent (the part) of a whole. */
    | { kind: "of"; part: Node; whole: Node };

export const int = (value: number): Node => ({ kind: "int", value });
export const dec = (units: number, places: number): Node => ({ kind: "dec", units, places });
export const frac = (n: number, d: number): Node => ({ kind: "frac", n, d });
export const pct = (value: number): Node => ({ kind: "pct", value });
export const bin = (op: Op, left: Node, right: Node): Node => ({ kind: "bin", op, left, right });
export const power = (base: Node, exp: 2 | 3 | 4 | 5): Node => ({ kind: "pow", base, exp });
export const root = (index: 2 | 3, radicand: Node): Node => ({ kind: "root", index, radicand });
export const of = (part: Node, whole: Node): Node => ({ kind: "of", part, whole });

/** The fraction glyphs a tile may show, keyed "n/d". */
export const FRACTION_GLYPHS: Record<string, string> = {
    "1/2": "½",
    "1/3": "⅓",
    "2/3": "⅔",
    "1/4": "¼",
    "3/4": "¾",
    "1/5": "⅕",
    "2/5": "⅖",
    "3/5": "⅗",
    "4/5": "⅘",
    "1/8": "⅛",
    "3/8": "⅜",
    "5/8": "⅝",
    "7/8": "⅞",
};

const SUPERSCRIPT = { 2: "²", 3: "³", 4: "⁴", 5: "⁵" } as const;
const ROOT_SIGN = { 2: "√", 3: "∛" } as const;

export function evaluate(node: Node): Rational {
    switch (node.kind) {
        case "int":
            return rat(node.value);
        case "dec":
            return rat(node.units, 10 ** node.places);
        case "frac":
            return rat(node.n, node.d);
        case "pct":
            return rat(node.value, 100);
        case "pow":
            return pow(evaluate(node.base), node.exp);
        case "root":
            return rationalRoot(evaluate(node.radicand), node.index);
        case "of":
            return mul(evaluate(node.part), evaluate(node.whole));
        case "bin": {
            const left = evaluate(node.left);
            const right = evaluate(node.right);
            switch (node.op) {
                case "+":
                    return add(left, right);
                case "−":
                    return sub(left, right);
                case "×":
                    return mul(left, right);
                default:
                    return div(left, right);
            }
        }
    }
}

const PRECEDENCE: Record<Op, number> = { "+": 1, "−": 1, "×": 2, "÷": 2 };

function isNegativeLiteral(node: Node): boolean {
    return node.kind === "int" && node.value < 0;
}

/** The tile's text. Uses U+2212 for subtraction and for a negative number. */
export function display(node: Node): string {
    switch (node.kind) {
        case "int":
            return node.value < 0 ? `−${-node.value}` : String(node.value);
        case "dec":
            return String(node.units / 10 ** node.places);
        case "frac":
            return FRACTION_GLYPHS[`${node.n}/${node.d}`] ?? `${node.n}/${node.d}`;
        case "pct":
            return `${node.value}%`;
        case "of":
            return `${display(node.part)} of ${display(node.whole)}`;
        case "root": {
            const radicand = display(node.radicand);
            const plain = node.radicand.kind === "int" && node.radicand.value >= 0;
            return `${ROOT_SIGN[node.index]}${plain ? radicand : `(${radicand})`}`;
        }
        case "pow": {
            const base = display(node.base);
            const plain = node.base.kind === "int" && node.base.value >= 0;
            return `${plain ? base : `(${base})`}${SUPERSCRIPT[node.exp]}`;
        }
        case "bin": {
            const mine = PRECEDENCE[node.op];
            const wrap = (child: Node, side: "left" | "right") => {
                const text = display(child);
                if (child.kind === "bin") {
                    const theirs = PRECEDENCE[child.op];
                    return theirs < mine || (theirs === mine && side === "right") ? `(${text})` : text;
                }
                if (child.kind === "of") {
                    return `(${text})`;
                }
                // A negative number is bracketed after an operator, and before a
                // × or ÷ (so "(−3) × (−4)", never "−3 × −4"); it leads a sum bare.
                if (isNegativeLiteral(child) && (side === "right" || mine === 2)) {
                    return `(${text})`;
                }
                return text;
            };
            return `${wrap(node.left, "left")} ${node.op} ${wrap(node.right, "right")}`;
        }
    }
}

/** The numbers a tile is built from, with a power's base but not its exponent, and a root's radicand. */
export function operands(node: Node): Rational[] {
    switch (node.kind) {
        case "bin":
            return [...operands(node.left), ...operands(node.right)];
        case "of":
            return [...operands(node.part), ...operands(node.whole)];
        case "pow":
            return operands(node.base);
        case "root":
            return operands(node.radicand);
        case "pct":
            // The number in front of the sign, so "50% of 100" is seen to contain 50.
            return [rat(node.value)];
        default:
            return [evaluate(node)];
    }
}

/** The whole numbers a tile is made of (not a fraction, decimal or percent), for sharing. */
export function wholeOperands(node: Node): number[] {
    switch (node.kind) {
        case "int":
            return [node.value];
        case "bin":
            return [...wholeOperands(node.left), ...wholeOperands(node.right)];
        case "of":
            return wholeOperands(node.whole);
        case "pow":
            return wholeOperands(node.base);
        case "root":
            return wholeOperands(node.radicand);
        default:
            return [];
    }
}

/** An operand's identity for "the same number appears elsewhere": ignores sign. */
export function operandKey(value: Rational): string {
    const magnitude = abs(value);
    return `${magnitude.n}/${magnitude.d}`;
}

/**
 * A key that is equal for two tiles that say the same thing, including
 * "3 + 5" and "5 + 3", so a board never holds both.
 */
export function canonKey(node: Node): string {
    switch (node.kind) {
        case "bin": {
            const parts = [canonKey(node.left), canonKey(node.right)];
            if (node.op === "+" || node.op === "×") {
                parts.sort();
            }
            return `(${parts[0]}${node.op}${parts[1]})`;
        }
        case "of":
            return `${canonKey(node.part)}of${canonKey(node.whole)}`;
        default:
            return display(node);
    }
}

/**
 * Tiles that give the game away or say nothing (ADR 0127 §2): a lone number,
 * an identity (+ 0, − 0, × 1, ÷ 1, 1 ×), or an operand equal to the value
 * ("12 + 0", "64 ÷ 8" for 8).
 */
export function isTrivial(node: Node): boolean {
    if (node.kind !== "bin" && node.kind !== "pow" && node.kind !== "root" && node.kind !== "of") {
        return true;
    }
    const value = evaluate(node);
    if (operands(node).some((operand) => equals(operand, value))) {
        return true;
    }
    return hasIdentity(node);
}

function literalValue(node: Node): number | null {
    return node.kind === "int" ? node.value : null;
}

function hasIdentity(node: Node): boolean {
    switch (node.kind) {
        case "bin": {
            const left = literalValue(node.left);
            const right = literalValue(node.right);
            const unit = (n: number | null) => n === 0 || n === 1 || n === -1;
            let identity = false;
            if (node.op === "+" || node.op === "−") {
                identity = left === 0 || right === 0;
            } else if (node.op === "×") {
                identity = unit(left) || unit(right);
            } else {
                identity = left === 0 || right === 1 || right === -1;
            }
            return identity || hasIdentity(node.left) || hasIdentity(node.right);
        }
        case "pow":
            return hasIdentity(node.base);
        case "of":
            return hasIdentity(node.whole);
        default:
            return false;
    }
}
